import * as stylex from '@stylexjs/stylex'
import { type StyleXStyles } from '@stylexjs/stylex'
import { type Dispatch, type ElementType, type SetStateAction, memo, type CSSProperties, type ComponentPropsWithoutRef, type ForwardedRef, type ReactNode } from 'react'
import isEqual from 'react-fast-compare'
import { typography } from '../tokens/typography.stylex'

type ParagraphComponentTag = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'span'
const defaultVariantMapping: Record<Variant, ParagraphComponentTag> = {
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  h5: 'h5',
  body: 'p',
  subtitle: 'h6',
  caption: 'p',
  tiny: 'p',
} as const

const typographyVariants = stylex.create({
  h1: {
    fontSize: typography.sizeH1,
    fontWeight: typography.weightSemibold,
    lineHeight: typography.lineHeightH1,
  },
  h2: {
    fontSize: typography.sizeH2,
    fontWeight: typography.weightSemibold,
    lineHeight: typography.lineHeightH2,
  },
  h3: {
    fontSize: typography.sizeH3,
    fontWeight: typography.weightSemibold,
    lineHeight: typography.lineHeightH3,
  },
  h4: {
    fontSize: typography.sizeH4,
    fontWeight: typography.weightSemibold,
    lineHeight: typography.lineHeightH4,
  },
  h5: {
    fontSize: typography.sizeH5,
    fontWeight: typography.weightSemibold,
    lineHeight: typography.lineHeightH5,
  },
  body: {
    fontSize: typography.sizeBody,
    fontWeight: typography.weightRegular,
    lineHeight: typography.lineHeightBody,
  },
  subtitle: {
    fontSize: typography.sizeSubtitle,
    fontWeight: typography.weightSemibold,
    lineHeight: typography.lineHeightSubtitle,
  },
  caption: {
    fontSize: typography.sizeCaption,
    fontWeight: typography.weightRegular,
    lineHeight: typography.lineHeightCaption,
  },
  tiny: {
    fontSize: typography.sizeTiny,
    fontWeight: typography.weightRegular,
    lineHeight: typography.lineHeightTiny,
  },
})

const baseStyles = stylex.create({
  root: {
    margin: 0,
    padding: 0,
    fontFamily: typography.font,
    letterSpacing: 0,
    opacity: 1,
    // NOTE: Overwrite for flatsome
    textTransform: 'none',
    width: 'auto',
  },
  noWrap: {
    whiteSpace: 'nowrap',
  },
  bold: {
    fontWeight: typography.weightSemibold,
  },
  italic: {
    fontStyle: 'italic',
  },
  ellipsis: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  lineThrough: {
    textDecoration: 'line-through',
  },
  lineClamp: (lineClamp: number) => ({
    overflow: 'hidden',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: lineClamp,
    display: '-webkit-box',
    textOverflow: 'ellipsis',
    /**
     * 防止英文沒有換行 ex: ABCDEFGH... 這樣的連續英文字
     */
    wordBreak: 'break-all',
  }),
  width: (width: NonNullable<CSSProperties['width']>) => ({
    width,
  }),
  align: (align: NonNullable<CSSProperties['textAlign']>) => ({
    textAlign: align,
  }),
  color: (color: string) => ({
    color,
  }),
  lineHeight: (lineHeight: NonNullable<CSSProperties['lineHeight']>) => ({
    lineHeight,
  }),
  fontWeight: (fontWeight: NonNullable<CSSProperties['fontWeight']>) => ({
    fontWeight,
  }),
  fontSize: (fontSize: NonNullable<CSSProperties['fontSize']>) => ({
    fontSize,
  }),
  verticalAlign: (verticalAlign: NonNullable<CSSProperties['verticalAlign']>) => ({
    verticalAlign,
  }),
  textDecoration: (textDecoration: NonNullable<CSSProperties['textDecoration']>) => ({
    textDecoration,
  }),
  userSelect: (userSelect: NonNullable<CSSProperties['userSelect']>) => ({
    userSelect,
  }),
})

type Variant = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'subtitle' | 'body' | 'caption' | 'tiny'

export type TextProps<RootComponent extends ElementType = 'p', TextElement extends HTMLElement = HTMLParagraphElement> = {
  /**
   * 根元素的 ref
   */
  rootRef?: ForwardedRef<TextElement> | Dispatch<SetStateAction<TextElement | null>>
  /**
   * 自訂渲染的 HTML 標籤，未指定時會依 variant 自動對應（h1→h1, body→p 等）
   */
  component?: RootComponent
  /**
   * 文字內容
   */
  children?: ReactNode
  /**
   * 排版變體，決定字體大小、粗細與行高
   * @default 'body'
   */
  variant?: Variant
  /**
   * 自訂根元素樣式（StyleX）
   */
  rootStyle?: StyleXStyles
  /**
   * 文字顏色
   * @default 'inherit'
   */
  color?: CSSProperties['color']
  /**
   * 文字對齊方式
   * @default 'left'
   */
  align?: CSSProperties['textAlign']
  /**
   * 元素寬度
   */
  width?: CSSProperties['width']
  /**
   * 是否禁止換行
   */
  noWrap?: boolean
  /**
   * 自訂行高，覆寫 variant 預設值
   */
  lineHeight?: CSSProperties['lineHeight']
  /**
   * 自訂字體大小，覆寫 variant 預設值
   */
  fontSize?: CSSProperties['fontSize']
  /**
   * 自訂字重，覆寫 variant 預設值
   */
  fontWeight?: CSSProperties['fontWeight']
  /**
   * 垂直對齊方式
   */
  verticalAlign?: CSSProperties['verticalAlign']
  /**
   * 文字裝飾樣式
   */
  textDecoration?: CSSProperties['textDecoration']
  /**
   * 是否粗體
   * @default false
   */
  bold?: boolean
  /**
   * 是否斜體
   * @default false
   */
  italic?: boolean
  /**
   * 是否顯示省略號（單行截斷）
   */
  ellipsis?: boolean
  /**
   * 多行截斷的行數，超出行數會顯示省略號
   */
  lineClamp?: number
  /**
   * 使用者是否可選取文字
   */
  userSelect?: CSSProperties['userSelect']
  /**
   * 是否顯示刪除線
   */
  lineThrough?: boolean
} & ComponentPropsWithoutRef<RootComponent>

/**
 * Text 用於顯示各種排版層級的文字內容。
 * 會根據 variant 自動對應合適的 HTML 標籤（h1~h5、p、span），
 * 並支援省略號截斷、行數限制、粗體/斜體等常用文字樣式。
 *
 * @example
 * ```tsx
 * <Text variant="h2">標題</Text>
 * <Text variant="body" bold>粗體內文</Text>
 * <Text variant="caption" ellipsis>很長的文字會被截斷...</Text>
 * ```
 */
export const TextComponent = <RootComponent extends ElementType = 'p', TextElement extends HTMLElement = HTMLParagraphElement>(props: TextProps<RootComponent, TextElement>) => {
  const { rootRef, children, variant = 'body', rootStyle, color = 'inherit', align = 'left', bold = false, italic = false, noWrap, component, lineHeight, fontWeight, fontSize, width, verticalAlign, textDecoration, ellipsis, lineClamp, userSelect, lineThrough, ...rest } = props
  const Component = component as ElementType || defaultVariantMapping[variant]
  return (
    <Component
      ref={rootRef}
      {...stylex.props(
        baseStyles.root,
        baseStyles.color(color),
        typographyVariants[variant],
        !!width && baseStyles.width(width),
        !!lineHeight && baseStyles.lineHeight(lineHeight),
        !!fontWeight && baseStyles.fontWeight(fontWeight),
        !!fontSize && baseStyles.fontSize(fontSize),
        !!verticalAlign && baseStyles.verticalAlign(verticalAlign),
        !!textDecoration && baseStyles.textDecoration(textDecoration),
        align && baseStyles.align(align),
        !!bold && baseStyles.bold,
        !!italic && baseStyles.italic,
        !!ellipsis && baseStyles.ellipsis,
        noWrap && baseStyles.noWrap,
        !!lineClamp && baseStyles.lineClamp(lineClamp),
        !!userSelect && baseStyles.userSelect(userSelect),
        !!lineThrough && baseStyles.lineThrough,
        rootStyle,
      )}
      {...rest}
    >
      {children}
    </Component>
  )
}

export const Text = memo(TextComponent, isEqual) as typeof TextComponent
